import { useState } from 'react';

import { useIntl } from 'react-intl';

import { GraphQLErrorMessages } from 'enums/graphql';
import { HttpErrorCodes } from 'remote/constants';
import { useAuthContext } from 'state/auth';
import { GraphQLErrorCodes, parseGraphQLErrorCodes } from 'utils/errors';

import { TabKey } from './types';

const USER_DOES_NOT_EXIST_ERROR = 'USER_DOES_NOT_EXIST_ERROR';

function isUserNotFoundGraphQLError(error: unknown) {
  return error instanceof Error && error.message === GraphQLErrorMessages.USER_NOT_FOUND;
}

function useTryToSignIn() {
  const { formatMessage } = useIntl();
  const { signIn } = useAuthContext();

  async function tryToSignIn({ email }: { email: string }) {
    return signIn({
      email,
      ...(email && { navigateOnSuccessParams: { email } }),
    })
      .then(() => {
        return { error: null };
      })
      .catch(error => {
        if (isUserNotFoundGraphQLError(error)) {
          return { error: USER_DOES_NOT_EXIST_ERROR };
        }
        if (
          parseGraphQLErrorCodes(error).some(
            parsedError => parsedError.errorCode === GraphQLErrorCodes.AUTH_EMAIL_NOT_REGISTERED
          )
        ) {
          return { error: USER_DOES_NOT_EXIST_ERROR };
        }

        if (error?.graphQLErrors?.[0]?.extensions?.statusCode === HttpErrorCodes.TooManyRequests) {
          return {
            error: `${formatMessage({ id: 'maxAttemptsReached' })} ${formatMessage({
              id: 'pleaseTryAgainLater',
            })}`,
          };
        }

        return {
          error: formatMessage({ id: 'authError' }),
        };
      });
  }

  return tryToSignIn;
}

function useSingleAuth() {
  const [email, setEmail] = useState('');
  const [activeTabKey, setActiveTabKey] = useState<TabKey>('singleAuth');
  const tryToSignIn = useTryToSignIn();

  async function handleAuthentication(email: string) {
    const { error: signInError } = await tryToSignIn({ email });
    if (signInError === null) {
      setActiveTabKey('signIn');
      return;
    }
    if (signInError !== USER_DOES_NOT_EXIST_ERROR) {
      throw new Error(signInError);
    }

    // Handle SignUp
    setEmail(email);
    setActiveTabKey('signUp');
  }

  return {
    email,
    activeTabKey,
    handleAuthentication,
  };
}

export default useSingleAuth;
